import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Trash2, Edit2, Package, Image as ImageIcon, RotateCw, XCircle, Plus, Download, CheckSquare, Square } from 'lucide-react';

const ShoeboxView = () => {
  const { shoeboxId } = useParams();
  const navigate = useNavigate();
  const [shoebox, setShoebox] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showShoeboxEditDialog, setShowShoeboxEditDialog] = useState(false);
  const [showAddToAlbumDialog, setShowAddToAlbumDialog] = useState(false);
  const [albums, setAlbums] = useState([]);
  const [selectedAlbumId, setSelectedAlbumId] = useState('');
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState([]);
  const [editForm, setEditForm] = useState({
    caption: '',
    location: '',
    original_date: ''
  });
  const [shoeboxEditForm, setShoeboxEditForm] = useState({
    name: '',
    tags: '',
    date_range: '',
    location: '',
    description: ''
  });

  useEffect(() => {
    fetchShoebox();
    fetchPhotos();
    fetchAlbums();
  }, [shoeboxId]);

  const fetchShoebox = async () => {
    try {
      const response = await axios.get(`${API}/shoeboxes/${shoeboxId}`);
      setShoebox(response.data);
      // Pre-fill shoebox edit form
      setShoeboxEditForm({
        name: response.data.name || '',
        tags: (response.data.tags || []).join(', '),
        date_range: response.data.date_range || '',
        location: response.data.location || '',
        description: response.data.description || ''
      });
    } catch (error) {
      toast.error('Failed to load shoebox');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API}/shoeboxes/${shoeboxId}/photos`);
      setPhotos(response.data);
    } catch (error) {
      toast.error('Failed to load photos');
    }
  };

  const fetchAlbums = async () => {
    try {
      const response = await axios.get(`${API}/albums`);
      setAlbums(response.data);
    } catch (error) {
      console.error('Failed to load albums');
    }
  };

  const handleEditPhoto = (photo) => {
    setSelectedPhoto(photo);
    setEditForm({
      caption: photo.caption || '',
      location: photo.location || '',
      original_date: photo.original_date || ''
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/photos/${selectedPhoto.id}`, editForm);
      toast.success('Photo updated successfully');
      setShowEditDialog(false);
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to update photo');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo from your library?')) return;

    try {
      await axios.delete(`${API}/photos/${photoId}`);
      toast.success('Photo deleted successfully');
      fetchPhotos();
      fetchShoebox();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handleDeleteShoebox = async () => {
    if (!window.confirm('Delete this shoebox? Photos will remain in your library.')) return;

    try {
      await axios.delete(`${API}/shoeboxes/${shoeboxId}`);
      toast.success('Shoebox deleted');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to delete shoebox');
    }
  };

  const handleSaveShoeboxMetadata = async () => {
    try {
      const tagsArray = shoeboxEditForm.tags
        .split(',')
        .map(t => t.trim())
        .filter(t => t);
      
      await axios.put(`${API}/shoeboxes/${shoeboxId}`, {
        name: shoeboxEditForm.name,
        tags: tagsArray,
        date_range: shoeboxEditForm.date_range,
        location: shoeboxEditForm.location,
        description: shoeboxEditForm.description
      });
      
      toast.success('Shoebox updated successfully');
      setShowShoeboxEditDialog(false);
      fetchShoebox();
    } catch (error) {
      toast.error('Failed to update shoebox');
    }
  };

  const handleRotatePhoto = async (photoId, degrees) => {
    try {
      await axios.post(`${API}/photos/${photoId}/rotate`, { degrees });
      toast.success(`Photo rotated ${degrees}° - Refreshing page...`);
      
      // Force full page reload to bypass all caching
      await new Promise(resolve => setTimeout(resolve, 1000));
      window.location.reload();
    } catch (error) {
      toast.error('Failed to rotate photo');
    }
  };

  const handleToggleUnusable = async (photo) => {
    try {
      await axios.put(`${API}/photos/${photo.id}`, {
        is_unusable: !photo.is_unusable
      });
      toast.success(photo.is_unusable ? 'Marked as usable' : 'Marked as unusable');
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to update photo');
    }
  };

  const handleAddToAlbum = (photo) => {
    setSelectedPhoto(photo);
    setShowAddToAlbumDialog(true);
  };

  const handleConfirmAddToAlbum = async () => {
    if (!selectedAlbumId) {
      toast.error('Please select an album');
      return;
    }

    try {
      await axios.post(`${API}/albums/${selectedAlbumId}/photos`, {
        photo_ids: [selectedPhoto.id]
      });
      toast.success('Photo added to album');
      setShowAddToAlbumDialog(false);
      setSelectedAlbumId('');
    } catch (error) {
      toast.error('Failed to add photo to album');
    }
  };

  const handleDownloadPhoto = async (photoId, filename) => {
    try {
      const response = await axios.get(`${API}/photos/${photoId}/image`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'photo.jpg');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Photo downloaded');
    } catch (error) {
      toast.error('Failed to download photo');
    }
  };

  const handleToggleBulkSelect = () => {
    setBulkSelectMode(!bulkSelectMode);
    setSelectedPhotoIds([]);
  };

  const handlePhotoCheckbox = (photoId) => {
    setSelectedPhotoIds(prev => 
      prev.includes(photoId) 
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  const handleBulkAddToAlbum = async () => {
    if (selectedPhotoIds.length === 0) {
      toast.error('No photos selected');
      return;
    }
    if (!selectedAlbumId) {
      toast.error('Please select an album');
      return;
    }

    try {
      await axios.post(`${API}/albums/${selectedAlbumId}/photos`, {
        photo_ids: selectedPhotoIds
      });
      toast.success(`Added ${selectedPhotoIds.length} photos to album`);
      setShowAddToAlbumDialog(false);
      setSelectedPhotoIds([]);
      setBulkSelectMode(false);
      setSelectedAlbumId('');
    } catch (error) {
      toast.error('Failed to add photos to album');
    }
  };

  const handleBulkMarkUnusable = async () => {
    if (selectedPhotoIds.length === 0) {
      toast.error('No photos selected');
      return;
    }

    try {
      await Promise.all(
        selectedPhotoIds.map(id => 
          axios.put(`${API}/photos/${id}`, { is_unusable: true })
        )
      );
      toast.success(`Marked ${selectedPhotoIds.length} photos as unusable`);
      fetchPhotos();
      setSelectedPhotoIds([]);
      setBulkSelectMode(false);
    } catch (error) {
      toast.error('Failed to mark photos as unusable');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPhotoIds.length === 0) {
      toast.error('No photos selected');
      return;
    }

    if (!window.confirm(`Delete ${selectedPhotoIds.length} photos from your library?`)) {
      return;
    }

    try {
      await Promise.all(
        selectedPhotoIds.map(id => axios.delete(`${API}/photos/${id}`))
      );
      toast.success(`Deleted ${selectedPhotoIds.length} photos`);
      fetchPhotos();
      fetchShoebox();
      setSelectedPhotoIds([]);
      setBulkSelectMode(false);
    } catch (error) {
      toast.error('Failed to delete photos');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-rose-50 to-blue-50">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/60 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white">
                  <Package className="w-6 h-6" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{shoebox?.name}</h1>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm text-gray-600">{photos.length} photos</p>
                    {shoebox?.location && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                        📍 {shoebox.location}
                      </span>
                    )}
                    {shoebox?.date_range && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                        📅 {shoebox.date_range}
                      </span>
                    )}
                  </div>
                  {shoebox?.tags && shoebox.tags.length > 0 && (
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {shoebox.tags.map((tag, idx) => (
                        <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => navigate('/library')}
                variant="default"
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white"
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                Go to Library for Advanced Features
              </Button>
              <Button
                onClick={() => setShowShoeboxEditDialog(true)}
                variant="outline"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit Details
              </Button>
              <Button
                onClick={handleDeleteShoebox}
                variant="outline"
                className="text-red-600 hover:bg-red-50 border-red-200"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Shoebox
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Photos Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {photos.length === 0 ? (
          <div className="text-center py-20">
            <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No photos in this shoebox</h3>
            <p className="text-gray-500">This shoebox is empty</p>
          </div>
        ) : (
          <div className="photo-grid">
            {photos.map((photo) => (
              <PhotoCard
                key={photo.id}
                photo={photo}
                onEdit={() => handleEditPhoto(photo)}
                onDelete={() => handleDeletePhoto(photo.id)}
                onRotate={(degrees) => handleRotatePhoto(photo.id, degrees)}
                onToggleUnusable={() => handleToggleUnusable(photo)}
              />
            ))}
          </div>
        )}
      </main>

      {/* Add to Album Dialog */}
      <Dialog open={showAddToAlbumDialog} onOpenChange={setShowAddToAlbumDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add to Album</DialogTitle>
            <DialogDescription>
              {selectedPhotoIds.length > 0 
                ? `Add ${selectedPhotoIds.length} selected photos to an album`
                : 'Choose an album to add this photo to'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="album-select">Select Album</Label>
              <select
                id="album-select"
                value={selectedAlbumId}
                onChange={(e) => setSelectedAlbumId(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="">Choose an album...</option>
                {albums.map((album) => (
                  <option key={album.id} value={album.id}>
                    {album.title} ({album.photo_count || 0} photos)
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-3 justify-end pt-4">
              <Button variant="outline" onClick={() => setShowAddToAlbumDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={selectedPhotoIds.length > 0 ? handleBulkAddToAlbum : handleConfirmAddToAlbum}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white"
              >
                Add to Album
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Photo Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Photo Details</DialogTitle>
            <DialogDescription>
              This information helps the Library Manager select photos for Picture Books
            </DialogDescription>
          </DialogHeader>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-xs text-blue-800">
              <strong>💡 Tip:</strong> Accurate captions, dates, and locations help AI select the best photos for your albums.
            </p>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="caption">Caption</Label>
              <Textarea
                id="caption"
                value={editForm.caption}
                onChange={(e) => setEditForm({ ...editForm, caption: e.target.value })}
                rows={3}
                placeholder="Enter a caption for this photo..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={editForm.location}
                onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                placeholder="e.g., Paris, France"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                value={editForm.original_date}
                onChange={(e) => setEditForm({ ...editForm, original_date: e.target.value })}
              />
            </div>
            <div className="flex gap-3 justify-end pt-4">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PhotoCard = ({ 
  photo, 
  onEdit,
  onDelete, 
  onRotate, 
  onToggleUnusable
}) => {
  const [error, setError] = useState(false);
  const [showRotateMenu, setShowRotateMenu] = useState(false);
  // Use updated_at for cache-busting (changes on rotation)
  const cacheBuster = photo.updated_at || photo.uploaded_at || Date.now();
  const imageUrl = `${API}/photos/${photo.id}/image?thumbnail=true&v=${encodeURIComponent(cacheBuster)}`;

  return (
    <div className={`bg-white rounded-lg overflow-hidden border-2 hover-lift group transition-all ${
      photo.is_unusable ? 'border-red-300 opacity-60' : 'border-gray-200'
    }`}>
      <div className="relative aspect-square">
        {/* Unusable Badge */}
        {photo.is_unusable && (
          <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded z-10">
            Do Not Use
          </div>
        )}

        {error ? (
          <div className="w-full h-full bg-red-100 flex items-center justify-center">
            <span className="text-red-600 text-xs">Failed to load</span>
          </div>
        ) : (
          <img
            key={`${photo.id}-${cacheBuster}`}
            src={imageUrl}
            alt={photo.caption || photo.filename}
            className="w-full h-full object-cover bg-gray-200"
            loading="lazy"
            onError={() => setError(true)}
          />
        )}

        {/* Simple Quick Actions */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="flex flex-col gap-2 w-3/4">
            {/* Rotate Options */}
            {showRotateMenu ? (
              <div className="bg-white rounded-lg p-3 space-y-2">
                <p className="text-sm font-semibold text-center text-gray-800 mb-2">Rotate Image</p>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRotate(90);
                    setShowRotateMenu(false);
                  }}
                  className="w-full text-sm justify-start"
                >
                  <RotateCw className="w-4 h-4 mr-2" />
                  90° CW (Clockwise)
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRotate(270);
                    setShowRotateMenu(false);
                  }}
                  className="w-full text-sm justify-start"
                >
                  <RotateCw className="w-4 h-4 mr-2 transform scale-x-[-1]" />
                  90° CCW (Counter-Clockwise)
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRotate(180);
                    setShowRotateMenu(false);
                  }}
                  className="w-full text-sm justify-start"
                >
                  <RotateCw className="w-4 h-4 mr-2" />
                  180° (Flip Upside Down)
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowRotateMenu(false);
                  }}
                  className="w-full text-sm mt-2 border-t pt-2"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowRotateMenu(true);
                  }}
                  className="bg-white/95 hover:bg-white"
                >
                  <RotateCw className="w-4 h-4 mr-2" />
                  Rotate
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  className="bg-white/95 hover:bg-white"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit Info
                </Button>
                <Button
                  size="sm"
                  variant={photo.is_unusable ? "default" : "secondary"}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleUnusable();
                  }}
                  className={photo.is_unusable 
                    ? "bg-green-600 text-white hover:bg-green-700" 
                    : "bg-white/95 hover:bg-white"
                  }
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  {photo.is_unusable ? 'Mark as Usable' : 'Do Not Use'}
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Clickable metadata section */}
      <div className="p-4 cursor-pointer hover:bg-gray-50 transition-colors" onClick={onEdit}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-900 font-medium mb-1 line-clamp-2">
              {photo.caption || photo.filename}
            </p>
            {photo.location && (
              <p className="text-xs text-gray-600 mb-1 truncate">{photo.location}</p>
            )}
            {photo.original_date && (
              <p className="text-xs text-gray-500">{photo.original_date}</p>
            )}
          </div>
          <Edit2 className="w-3 h-3 text-gray-400 flex-shrink-0 mt-1" />
        </div>
        <p className="text-xs text-blue-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
          Click to edit caption, date, location
        </p>
      </div>
    </div>
  );
};

export default ShoeboxView;
